import type { OfferReviewStatusType, OfferStructureType } from '@/api/deal/types';

export const DEMAND_STATUS = {
  Active: 'Active',
  Removed: 'Removed',
} as const;
export type DemandStatus = (typeof DEMAND_STATUS)[keyof typeof DEMAND_STATUS];

export const DEMAND_STATUS_FILTER = {
  Active: 'Active',
  Removed: 'Removed',
  All: 'All',
} as const;
export type DemandStatusFilter = (typeof DEMAND_STATUS_FILTER)[keyof typeof DEMAND_STATUS_FILTER];

export const DEMAND_ORDER_BY = {
  Company: 'Company',
  InterestedParty: 'InterestedParty',
  Amount: 'Amount',
  TargetValuation: 'TargetValuation',
  ConfidenceScore: 'ConfidenceScore',
  Status: 'Status',
  Created: 'Created',
  PostedBy: 'PostedBy',
} as const;
export type DemandOrderBy = (typeof DEMAND_ORDER_BY)[keyof typeof DEMAND_ORDER_BY];

export const ORDER_DIRECTION = {
  Asc: 'Asc',
  Desc: 'Desc',
} as const;
export type OrderDirection = (typeof ORDER_DIRECTION)[keyof typeof ORDER_DIRECTION];

export const INTERESTED_PARTY_TYPE = {
  Individual: 'Individual',
  Entity: 'Entity',
} as const;
export type InterestedPartyType =
  (typeof INTERESTED_PARTY_TYPE)[keyof typeof INTERESTED_PARTY_TYPE];
export type DemandConfidenceScore = string;

export interface DemandIntroducer {
  id: string;
  name: string;
}

export interface DemandEntryListItem {
  id: string;
  companyId: string;
  companyName: string;
  companyLogoImageId: string | null;
  interestedPartyId: string;
  interestedPartyType: InterestedPartyType;
  interestedPartyName: string;
  amount: number;
  targetValuation: number | null;
  confidenceScore: DemandConfidenceScore | null;
  introducers: DemandIntroducer[];
  /** Absent until the API stores it (BE-4); read as "not recorded". */
  structuresPermitted?: OfferStructureType[];
  status: DemandStatus;
  /** Not sent by the admin demand list yet; an open entry without it reads as Public. */
  reviewStatus?: OfferReviewStatusType | null;
  postedById: string;
  hasNotes: boolean;
  notesPreview: string | null;
  createdAt: string;
  updatedAt: string;
  removedAt: string | null;
}

export interface DemandPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface DemandListRes {
  demandEntries: DemandEntryListItem[];
  pagination: DemandPaginationMeta;
}

export type DemandSummaryParams = Pick<
  GetDemandEntriesParams,
  'search' | 'companyIds' | 'interestedPartyIds' | 'introducerIds'
>;

export interface DemandSummaryRes {
  activeEntries: number;
  totalDemandInUsd: number;
}

export interface CompanyFilterOption {
  id: string;
  name: string;
}

export interface InterestedPartyFilterOption {
  id: string;
  type: InterestedPartyType;
  name: string;
}

export interface IntroducerFilterOption {
  id: string;
  name: string;
}

export interface DemandFilterOptionsRes {
  companies: CompanyFilterOption[];
  interestedParties: InterestedPartyFilterOption[];
  introducers: IntroducerFilterOption[];
}

export interface GetDemandEntriesParams {
  page?: number;
  limit?: number;
  search?: string;
  companyIds?: string[];
  interestedPartyIds?: string[];
  introducerIds?: string[];
  status?: DemandStatusFilter;
  orderBy?: DemandOrderBy;
  orderDirection?: OrderDirection;
}

export const DEMAND_CONFIDENCE_OPTIONS = ['Unknown', 'Moderate', 'High', 'Confirmed'] as const;
export type DemandConfidenceOption = (typeof DEMAND_CONFIDENCE_OPTIONS)[number];

export interface DemandEntryDetail {
  id: string;
  companyId: string;
  companyName: string;
  interestedPartyId: string;
  interestedPartyType: InterestedPartyType;
  interestedPartyName: string;
  amount: number;
  targetValuation: number | null;
  confidenceScore: DemandConfidenceScore | null;
  introducers: DemandIntroducer[];
  structuresPermitted?: OfferStructureType[];
  notes: string | null;
  status: DemandStatus;
  postedById: string;
  createdAt: string;
  updatedAt: string;
  removedAt: string | null;
  removedById: string | null;
}

export interface DemandEntryPayload {
  companyId: string;
  companyName: string;
  interestedPartyId: string;
  interestedPartyType: InterestedPartyType;
  interestedPartyName: string;
  amount: number;
  confidenceScore?: DemandConfidenceOption;
  targetValuation?: number;
  introducers?: DemandIntroducer[];
  structuresPermitted?: OfferStructureType[];
  notes?: string;
}

export interface CompanyDemandEntryPayload {
  interestedPartyId: string;
  interestedPartyType: InterestedPartyType;
  interestedPartyName: string;
  amount: number;
  targetValuation?: number;
  confidenceScore?: DemandConfidenceOption;
  introducers?: DemandIntroducer[];
  structuresPermitted?: OfferStructureType[];
  notes?: string;
}
